import { isEqual, throttle } from 'lodash-es';
import { startTransition, useEffect, useState } from 'react';

export interface ScrollPosition {
  /**
   * The vertical position of the top of the current visible section, in number of pixels
   * from the top of the iframe. This number will be minimum 0 and maximum the height of the
   * iframe.
   */
  top: number;

  /**
   * The vertical position of the bottom of the current visible section, in number of pixels
   * from the top of the iframe. This number will be minimum 0 and maximum the height of the
   * iframe.
   */
  bottom: number;
}

export type ScrollPositionHandler = (args: ScrollPosition) => void;

const defaultPosition: ScrollPosition = { top: 0, bottom: 0 };
const CONTAINER_HEIGHT = 100;
const threshold = [...Array(CONTAINER_HEIGHT + 1).keys()].map(
  (i) => i / CONTAINER_HEIGHT
);

const containerData = new Map<
  HTMLElement | Window,
  {
    handlers: Set<ScrollPositionHandler>;
    destroy: () => void;
    position: ScrollPosition;
  }
>();

function createScrollPositionHandler(container: HTMLElement | Window) {
  const elementContainer = document.createElement('div');
  Object.assign(elementContainer.style, {
    position: container === window ? 'fixed' : 'absolute',
    top: '0',
    bottom: '0',
    width: '100%',
    pointerEvents: 'none',
    overflow: 'hidden',
  });
  (container === window ? document.body : (container as HTMLElement)).appendChild(
    elementContainer
  );

  const elements: Array<{
    el: HTMLDivElement;
    entry?: IntersectionObserverEntry;
  }> = [];
  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const element = elements.find(({ el }) => el === entry.target);
        if (element) {
          element.entry = entry;
        }
      }

      const intersectingElements = elements.filter(
        (element) => element.entry && element.entry.intersectionRatio > 0
      );
      const data = containerData.get(container);
      if (data) {
        let newPosition;
        if (intersectingElements.length > 0) {
          const containerRect = elementContainer.getBoundingClientRect();
          newPosition = {
            top: intersectingElements[0].entry!.intersectionRect.top - containerRect.top,
            bottom:
              intersectingElements[intersectingElements.length - 1].entry!
                .intersectionRect.bottom - containerRect.top,
          };
        } else {
          newPosition = defaultPosition;
        }

        if (
          newPosition.top !== data.position.top ||
          newPosition.bottom !== data.position.bottom
        ) {
          data.position = newPosition;
          for (const handler of data.handlers) {
            handler(data.position);
          }
        }
      }
    },
    { threshold }
  );

  const resizeObserver = new ResizeObserver(() => {
    const count = Math.ceil(elementContainer.offsetHeight / CONTAINER_HEIGHT);
    for (let i = 0; i < count; i++) {
      if (!elements[i]) {
        const el = document.createElement('div');
        elements[i] = { el };
        Object.assign(el.style, {
          position: 'absolute',
          top: `${i * CONTAINER_HEIGHT}px`,
          height: `${CONTAINER_HEIGHT}px`,
          width: '100%',
        });
        elementContainer.appendChild(el);
        intersectionObserver.observe(el);
      }
    }
  });
  resizeObserver.observe(elementContainer);

  return () => {
    resizeObserver.disconnect();
    intersectionObserver?.disconnect();
    elementContainer.remove();
  };
}

/**
 * Registers an intersection handler that detects the scrolled position of the current
 * iframe within the browser viewport and calls a handler when it is first invoked and
 * whenever the scrolled position changes. This allows to position elements within the
 * iframe in a way that their position stays sticky in relation to the browser window.
 * @param handler Is invoked when the function is first called and whenever the scroll
 * position changes (for example due to the user scrolling the parent document). The
 * "top" parameter is the number of pixels from the top of the browser viewport to the
 * top of the iframe (if the top of the iframe is above the top of the browser viewport)
 * or 0 (if the top of the iframe is below the top of the browser viewport). Positioning
 * an element absolutely at this top position inside the iframe will simulate a sticky
 * positioning at the top edge of the browser viewport.
 * @returns Returns a callback that unregisters the handler.
 */
function registerScrollPositionHandler(
  handler: ScrollPositionHandler,
  container: HTMLElement | Window = document.body
): () => void {
  const data = containerData.get(container);
  if (data) {
    data.handlers.add(handler);
    handler(data.position);
  } else {
    containerData.set(container, {
      handlers: new Set([handler]),
      destroy: createScrollPositionHandler(container),
      position: defaultPosition,
    });
  }

  return () => {
    const listener = containerData.get(container);
    if (listener) {
      listener.handlers.delete(handler);
      if (listener.handlers.size === 0) {
        listener.destroy();
        containerData.delete(container);
      }
    }
  };
}

function getCurrentPosition(container: HTMLElement | Window | undefined): ScrollPosition {
  return (container && containerData.get(container)?.position) ?? defaultPosition;
}

function roundScrollPosition(
  scrollPosition: ScrollPosition,
  { batchHeight = 1, margin = 0 }: { batchHeight?: number; margin?: number } = {}
): ScrollPosition {
  return {
    top: Math.floor(scrollPosition.top / batchHeight) * batchHeight - margin,
    bottom:
      Math.ceil(scrollPosition.top / batchHeight) * batchHeight +
      (scrollPosition.bottom - scrollPosition.top) +
      margin,
  };
}

export function useScrollPosition({
  isDisabled,
  container = typeof document !== 'undefined' ? document.body : undefined,
  throttleMs,
  batchHeight,
  margin,
}: {
  isDisabled?: boolean;
  container?: HTMLElement | Window;
  /** If specified, the scroll position updates will be throttled to the given number of milliseconds. */
  throttleMs?: number;
  /** If specified, not every scroll will cause an update, but the scroll position will be rounded to a grid of this height. */
  batchHeight?: number;
  /** If specified, add this number of pixels to the top and bottom (to render a bit more than what is currently visible). */
  margin?: number;
} = {}): ScrollPosition {
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>(() =>
    roundScrollPosition(getCurrentPosition(container), { batchHeight, margin })
  );
  useEffect(() => {
    if (!isDisabled) {
      const handler = (pos: ScrollPosition) => {
        startTransition(() => {
          setScrollPosition((prevScrollPosition) => {
            const rounded = roundScrollPosition(pos, { batchHeight, margin });
            if (isEqual(prevScrollPosition, rounded)) {
              return prevScrollPosition;
            } else {
              return rounded;
            }
          });
        });
      };
      return registerScrollPositionHandler(
        throttleMs ? throttle(handler, throttleMs) : handler,
        container
      );
    }
  }, [batchHeight, container, isDisabled, margin, throttleMs]);
  return scrollPosition;
}

type WindowScrollPosition = {
  left: number;
  top: number;
};
/**
 * Returns the current scroll position of the browser window.
 */
export function useWindowScrollPosition({
  isDisabled,
  container,
}: { isDisabled?: boolean; container?: HTMLElement } = {}): WindowScrollPosition {
  const [scrollPosition, setScrollPosition] = useState<WindowScrollPosition>({
    left: 0,
    top: 0,
  });
  useEffect(() => {
    if (isDisabled) {
      return () => {};
    }

    const handleScroll = () => {
      startTransition(() => {
        setScrollPosition({
          left: container?.scrollLeft ?? window.scrollX,
          top: container?.scrollTop ?? window.scrollY,
        });
      });
    };

    (container ?? window)?.addEventListener('scroll', handleScroll);

    return () => (container ?? window)?.removeEventListener('scroll', handleScroll);
  }, [container, isDisabled]);

  return scrollPosition;
}
